import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Container, Typography, TextField, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Stack, Chip, Tooltip, IconButton,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// 8. Boedel List (Estate List): an automatic, always-current overview for the Admin User of
// every matter's progress - every non-deleted matter appears on its own, with no per-matter
// opt-in required. Data comes straight from GET /api/boedel-list, so whatever's already been
// captured on the matter (dates, appointments, accounts) shows up here without anyone having
// to re-enter it or open the matter.
//
// Two things ARE edited directly from this list, without opening the matter:
// - "Noted": a free-running status note (e.g. "L&D Account is being attended to"), backed by
//   the same `comment` field shown on the matter's Estate Information tab.
// - "My List": an optional per-user bookmark (backed by legal_matter.boedelUserId) purely for
//   personal filtering - it's not a precondition for a matter showing up at all, just a way
//   to narrow the view down to "mine" via the All/My List toggle below.

const fmtDate = (d) => {
    if (!d) return '-';
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString('en-ZA');
};
const fmtMoney = (v) => (v ? `R ${parseFloat(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-');

// Best-effort status colour heuristic, based on how far along the matter's milestones are.
// This is a reasonable default, not a reproduction of any specific manual colour rule -
// adjust STATUS_STAGES below if the practice's actual trigger logic differs.
const STATUS_STAGES = [
    { key: 'new', label: 'New File', color: '#fff59d', test: (r) => !r.provAppointment },
    { key: 'meetings', label: 'Meetings Stage', color: '#80deea', test: (r) => !r.ldAccountDate },
    { key: 'accounts', label: 'L&D / Accounts Stage', color: '#ffcc80', test: (r) => !r.confirmation },
    { key: 'confirmed', label: 'Confirmed - Awaiting Release', color: '#ce93d8', test: (r) => !r.release },
    { key: 'released', label: 'Released', color: '#a5d6a7', test: () => true },
];
const getStageForRow = (row) => STATUS_STAGES.find(s => s.test(row)) || STATUS_STAGES[STATUS_STAGES.length - 1];

const COLUMNS = [
    { key: 'myList', label: 'My List' },
    { key: 'estateName', label: 'Estate Name' },
    { key: 'masterReference', label: "Master's Reference" },
    { key: 'noted', label: 'Noted' },
    { key: 'attorneys', label: 'Attorneys' },
    { key: 'trusteeLiquidator', label: 'Trustee/Liquidator' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'bond', label: 'Bond', money: true },
    { key: 'bondReduction', label: 'Bond Reduction', date: true },
    { key: 'provAppointment', label: 'Prov. Appointment', date: true },
    { key: 'finalAppointment', label: 'Final Appointment', date: true },
    { key: 'magistrate', label: 'Magistrate' },
    { key: 'firstMeeting', label: '1st Meeting', date: true },
    { key: 'secondMeeting', label: '2nd Meeting', date: true },
    { key: 'adoptedResolutions', label: 'Adopted Reso' },
    { key: 'specialMeeting', label: 'Special Meeting', date: true },
    { key: 'ldAccountDate', label: 'L&D Acct Date', date: true },
    { key: 'extension', label: 'Extension', date: true },
    { key: 'ldSubmission', label: 'L&D Submission', date: true },
    { key: 'querySheet', label: 'Query Sheet', date: true },
    { key: 'preliminaryRequirements', label: 'Preliminary Requirements' },
    { key: 'finalDate', label: 'Final Date', date: true },
    { key: 'preConfirmation', label: 'Pre-Confirmation' },
    { key: 'confirmation', label: 'Confirmation', date: true },
    { key: 'form5', label: 'Form 5', date: true },
    { key: 'fees', label: 'Fees', money: true },
    { key: 'iconFee', label: 'Icon Fee', money: true },
    { key: 'iconInvoice', label: 'Icon Invoice' },
    { key: 'postConfirmation', label: 'Post-Confirmation' },
    { key: 'specialFeeApplication', label: 'Special Fee Application' },
    { key: 'release', label: 'Release', date: true },
    { key: 'destruction', label: 'Destruction', date: true },
    { key: 'form7', label: 'Form 7', date: true },
];

export default function BoedelListPage() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [savingNoteId, setSavingNoteId] = useState(null);
    const [scope, setScope] = useState('all'); // 'all' | 'mine' - "all" is the automatic default
    const [togglingListId, setTogglingListId] = useState(null);

    const fetchData = async (currentUser, currentScope) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ scope: currentScope || 'all' });
            if (currentUser?.id) params.set('userId', currentUser.id);
            const res = await axios.get(`/api/boedel-list?${params.toString()}`, { withCredentials: true });
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to load Boedel List', err);
            setError('Failed to load the Boedel List. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleScopeChange = (e, newScope) => {
        if (!newScope || newScope === scope) return;
        setScope(newScope);
        if (user) fetchData(user, newScope);
    };

    // "My List" is a single-user assignment (legal_matter.boedelUserId) kept purely as an
    // optional personal filter - toggling it here just adds/removes the current user as that
    // matter's assignee, it never affects whether the matter appears under "All Matters".
    const handleToggleMyList = async (row, e) => {
        if (e) e.stopPropagation();
        if (!user) return;
        const isMine = String(row.boedelUserId) === String(user.id);
        setTogglingListId(row.id);
        try {
            await axios.put(`/api/legal-matter/${row.id}`, { boedelUserId: isMine ? '' : user.id }, { withCredentials: true });
            setRows(prev => {
                const updated = prev.map(r => r.id === row.id
                    ? { ...r, boedelUserId: isMine ? null : user.id, boedelUserName: isMine ? '' : `${user.firstName || ''} ${user.lastName || ''}`.trim() }
                    : r);
                // If we're viewing "My List" and just removed this matter from it, drop it
                // from view immediately instead of waiting for the next refresh.
                return scope === 'mine' && isMine ? updated.filter(r => r.id !== row.id) : updated;
            });
        } catch (err) {
            console.error('Failed to update My List status', err);
            window.alert('Failed to update. Please try again.');
        } finally {
            setTogglingListId(null);
        }
    };

    const handleStartEditNote = (row, e) => {
        e.stopPropagation();
        setEditingNoteId(row.id);
        setNoteDraft(row.noted || '');
    };

    const handleCancelEditNote = (e) => {
        e.stopPropagation();
        setEditingNoteId(null);
        setNoteDraft('');
    };

    const saveNote = async (rowId, newValue, e) => {
        if (e) e.stopPropagation();
        setSavingNoteId(rowId);
        try {
            await axios.put(`/api/legal-matter/${rowId}`, { comment: newValue }, { withCredentials: true });
            setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, noted: newValue } : r)));
            setEditingNoteId(null);
            setNoteDraft('');
        } catch (err) {
            console.error('Failed to save note', err);
            // Don't use the page-level `error` state here - that hides the whole table,
            // and a failed note save shouldn't take the rest of the list down with it.
            window.alert('Failed to save the note. Please try again.');
        } finally {
            setSavingNoteId(null);
        }
    };

    const handleClearNote = (row, e) => {
        e.stopPropagation();
        if (!window.confirm('Remove this note?')) return;
        saveNote(row.id, '', e);
    };

    useEffect(() => {
        axios.get('/api/user', { withCredentials: true })
            .then((res) => {
                setUser(res.data);
                if (res.data?.role === 'guest') return; // 7.7-style: guests don't get a cross-matter view
                fetchData(res.data, 'all');
            })
            .catch((err) => {
                console.error('Failed to load user', err);
                setError('Failed to load your user session.');
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredRows = useMemo(() => {
        if (!search.trim()) return rows;
        const term = search.toLowerCase();
        return rows.filter((r) =>
            (r.estateName || '').toLowerCase().includes(term) ||
            (r.masterReference || '').toLowerCase().includes(term) ||
            (r.attorneys || '').toLowerCase().includes(term) ||
            (r.trusteeLiquidator || '').toLowerCase().includes(term)
        );
    }, [rows, search]);

    if (user?.role === 'guest') {
        return (
            <Container maxWidth={false} sx={{ mb: 5 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Boedel List</Typography>
                <Typography color="text.secondary">This cross-matter view is not available to guest users.</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} sx={{ mb: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6">Boedel List</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Every matter, updated automatically from Estate Information, L&D Accounts, Bond Security and Meetings - nothing here needs to be re-entered.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => user && fetchData(user, scope)} disabled={loading}>
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        disabled={!user}
                        onClick={() => window.open(`/api/boedel-list/download-pdf?scope=${scope}&userId=${user.id}`, '_blank')}
                    >
                        Print / Export PDF
                    </Button>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                <TextField
                    size="small"
                    label="Search by estate name, master's reference, attorneys, or trustee/liquidator"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 340, flexGrow: 1, maxWidth: 600 }}
                />
                <ToggleButtonGroup size="small" exclusive value={scope} onChange={handleScopeChange}>
                    <ToggleButton value="all">All Matters</ToggleButton>
                    <ToggleButton value="mine">My List</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {/* Status colour legend - calculated automatically from each matter's own progress
                (see STATUS_STAGES) - nothing here is set manually. */}
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Status (automatic):</Typography>
                {STATUS_STAGES.map((s) => (
                    <Chip key={s.key} label={s.label} size="small" sx={{ bgcolor: s.color, fontWeight: 'bold' }} />
                ))}
            </Stack>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: '75vh' }}>
                        <Table size="small" stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap', fontSize: '0.72rem', py: 0.5 }, '& td.noted-cell, & th.noted-cell': { whiteSpace: 'normal' } }}>
                            <TableHead>
                                <TableRow>
                                    {COLUMNS.map((col) => (
                                        <TableCell key={col.key} className={col.key === 'noted' ? 'noted-cell' : undefined} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', minWidth: col.key === 'noted' ? 220 : undefined }}>{col.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRows.map((row) => {
                                    const stage = getStageForRow(row);
                                    return (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            sx={{ cursor: 'pointer', bgcolor: stage.color + '55' }}
                                            onClick={() => navigate(`/matter/${row.id}`)}
                                        >
                                            {COLUMNS.map((col) => {
                                                if (col.key === 'myList') {
                                                    const isMine = String(row.boedelUserId) === String(user?.id);
                                                    const isToggling = togglingListId === row.id;
                                                    const tooltipTitle = isToggling
                                                        ? 'Updating…'
                                                        : isMine
                                                            ? 'On your list - click to remove'
                                                            : row.boedelUserId
                                                                ? `On ${row.boedelUserName || "another user's"} list - click to move to yours`
                                                                : 'Add to your list';
                                                    return (
                                                        <TableCell key={col.key} align="center" onClick={(e) => e.stopPropagation()}>
                                                            <Tooltip title={tooltipTitle}>
                                                                <span>
                                                                    <IconButton size="small" disabled={isToggling} onClick={(e) => handleToggleMyList(row, e)}>
                                                                        {isToggling ? <CircularProgress size={16} /> : isMine ? (
                                                                            <BookmarkIcon fontSize="small" color="primary" />
                                                                        ) : (
                                                                            <BookmarkBorderIcon fontSize="small" />
                                                                        )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    );
                                                }
                                                if (col.key === 'noted') {
                                                    const isEditing = editingNoteId === row.id;
                                                    const isSaving = savingNoteId === row.id;
                                                    return (
                                                        <TableCell key={col.key} className="noted-cell">
                                                            {isEditing ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="flex-start" onClick={(e) => e.stopPropagation()}>
                                                                    <TextField
                                                                        autoFocus
                                                                        multiline
                                                                        size="small"
                                                                        placeholder="e.g. L&D Account is being attended to"
                                                                        value={noteDraft}
                                                                        onChange={(e) => setNoteDraft(e.target.value)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                                                e.preventDefault();
                                                                                saveNote(row.id, noteDraft, e);
                                                                            } else if (e.key === 'Escape') {
                                                                                handleCancelEditNote(e);
                                                                            }
                                                                        }}
                                                                        sx={{ minWidth: 200 }}
                                                                        disabled={isSaving}
                                                                    />
                                                                    <IconButton size="small" color="primary" disabled={isSaving} onClick={(e) => saveNote(row.id, noteDraft, e)}>
                                                                        <CheckIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" disabled={isSaving} onClick={handleCancelEditNote}>
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            ) : (
                                                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                                                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{row.noted || <em style={{ color: '#999' }}>No note</em>}</Typography>
                                                                    <Stack direction="row" spacing={0}>
                                                                        <Tooltip title="Edit note">
                                                                            <IconButton size="small" onClick={(e) => handleStartEditNote(row, e)}>
                                                                                <EditIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        {row.noted && (
                                                                            <Tooltip title="Remove note">
                                                                                <IconButton size="small" onClick={(e) => handleClearNote(row, e)}>
                                                                                    <DeleteIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Stack>
                                                                </Stack>
                                                            )}
                                                        </TableCell>
                                                    );
                                                }
                                                const raw = row[col.key];
                                                const display = col.money ? fmtMoney(raw) : col.date ? fmtDate(raw) : (raw || '-');
                                                return (
                                                    <TableCell key={col.key}>
                                                        {col.key === 'estateName' ? (
                                                            <Tooltip title={stage.label}>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{display}</Typography>
                                                            </Tooltip>
                                                        ) : display}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                                {filteredRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                            No matters found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
}
